import React from 'react';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { SaveButton } from 'components/UIElements/UIButton';
import { useAutoFocus } from 'utils/hooks/focus';
import { getDayCountedByDrawer } from 'reducers/configs/settings';
import CloseButton from 'components/CustomButtons/CloseButton';
import { getCustomPaymentTypes } from 'reducers/PaymentTypes';
import { getCheckOnlyCashOnEOD, getShowExpected } from 'reducers/OpenCloseDay';
import { ReasonCodes } from 'types/ReasonCodes';

import { TendersByCurrency } from '../OCDTypes';
import TenderMobile from '../components/TenderMobile';
import TenderDesktop from '../components/TenderDesktop';

const usePaymentTypes = (
  expected: { type: string; expected: number }[],
): { name?: string; type: string; expected: number }[] => {
  const others = expected.filter(e => !/^CARD-/.test(e.type));
  const cards = expected.filter(e => /^CARD-/.test(e.type));
  const giftCards = expected.filter(e => /^GIFTCARD-/.test(e.type));
  const possibleCustomPaymentTypes = useSelector(getCustomPaymentTypes);

  return [
    // CASH
    others.find(({ type }) => type === 'CASH') ?? {
      type: 'CASH',
      expected: 0,
    },

    // All cards sorted by type
    ...R.pipe(
      R.map(({ type, expected }) => ({
        name: type.replace(/CARD-/, '') || 'CARD',
        type,
        expected,
      })),
      R.sortBy(R.prop('name')),
    )(cards),

    // Other special types
    {
      type: 'TRANSFER',
      expected: (others.find(o => o.type === 'TRANSFER') || { expected: 0 })
        .expected,
    },

    // Custom gift card based tenders
    ...R.pipe(
      R.map(({ type, expected }) => ({
        name: type.replace('GIFTCARD-', '') || 'GIFTCARD',
        type,
        expected,
      })),
      R.sortBy(R.prop('name')),
    )(giftCards),

    {
      type: 'CHECK',
      expected: (others.find(o => o.type === 'CHECK') || { expected: 0 })
        .expected,
    },

    // All custom payment tenders
    ...R.pipe(
      R.map(pt => ({
        name: pt.name,
        type: pt.type,
        expected: (others.find(o => o.type === pt.type) || { expected: 0 })
          .expected,
      })),
      R.sortBy(R.prop('name')),
    )(possibleCustomPaymentTypes),
  ];
};

const useStyles = makeStyles({
  table: {
    'table-layout': 'fixed',
    '& td, & th': {
      'border-bottom': 'none',
    },
    '& th': {
      'font-weight': 700,
    },
  },
});

interface Props {
  onChange: (type: string, newVal: { counted: string; reason: number }) => void;
  onDone: () => void;
  onCancel: () => void;
  reasons?: ReasonCodes[];
  missingReasons?: string[];
  expected?: { [tender: string]: number };
  tenders?: TendersByCurrency[string];
  selectedCurrency: string;
  billsTotal?: string;
}

const Tenders: React.FC<Props> = ({
  onChange,
  onDone,
  onCancel,
  missingReasons = [],
  expected = {},
  tenders = {},
  selectedCurrency,
  billsTotal = '',
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktopView = useMediaQuery(theme.breakpoints.up('sm'));

  const { t } = useTranslation('openCloseDay', {
    keyPrefix: 'closeDay.tenders',
  });
  const { t: tCommon } = useTranslation('common');

  const drawerCountUsed = useSelector(getDayCountedByDrawer);

  const expectedTenders = Object.entries(expected).map(([type, expected]) => ({
    type,
    expected,
  }));

  const showExpected = useSelector(getShowExpected);
  const checkOnlyCash = useSelector(getCheckOnlyCashOnEOD);

  const paymentTypes = usePaymentTypes(expectedTenders).filter(({ type }) =>
    checkOnlyCash ? type === 'CASH' : type,
  );

  useAutoFocus('firstTender');

  const tenderRows = paymentTypes.map(({ name, type, expected }) => ({
    name,
    type,
    expected,
    counted: '',
    reason: -1,
    ...((tenders[type] as typeof tenders[string] | undefined | {}) || {}),
  }));

  return (
    <div data-testid="tenders">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingBottom="8px"
        paddingTop="2px"
        marginX="14px"
      >
        <Box
          display="flex"
          flexDirection="column"
          flexWrap="nowrap"
          paddingTop="1em"
        >
          <Box
            component="span"
            fontSize="1.75em"
            fontWeight={700}
            data-testid="register-drawer-title"
          >
            {drawerCountUsed ? t('drawerTitle') : t('registerTitle')}{' '}
          </Box>
          <Box component="span" data-testid="pos-currency">
            {t('currencyCode', { code: selectedCurrency })}
          </Box>
        </Box>
        <Box display="flex">
          <SaveButton
            text={tCommon('done')}
            data-testid="submit-btn"
            variant="POS"
            // @ts-ignore (remove when UIButton refactor reaches master-fixes)
            onClick={onDone}
          />
          <CloseButton data-testid="close-btn" action={onCancel} />
        </Box>
      </Box>
      <Box padding="1rem">
        {isDesktopView ? (
          <Table className={classes.table}>
            <TableHead data-testid="headers" className="tenders_thead">
              <TableRow>
                <TableCell data-testid="type-header">
                  {t('headers.type')}
                </TableCell>
                <TableCell data-testid="counted-header">
                  {t('headers.counted')}
                </TableCell>
                {showExpected ? (
                  <>
                    <TableCell data-testid="expected-header">
                      {t('headers.expected')}
                    </TableCell>
                    <TableCell data-testid="diff-header">
                      {t('headers.difference')}
                    </TableCell>
                    <TableCell data-testid="reason-header">
                      {t('headers.reason')}
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {tenderRows.map(
                ({ name, type, expected, counted, reason }, i) => (
                  <TenderDesktop
                    key={type}
                    onChange={onChange}
                    isMissingReason={missingReasons.includes(type)}
                    name={name}
                    type={type}
                    expected={expected}
                    counted={counted}
                    reason={reason}
                    isFirst={i === 0}
                    billsTotal={billsTotal}
                  />
                ),
              )}
            </TableBody>
          </Table>
        ) : (
          tenderRows.map(({ name, type, expected, counted, reason }, i) => (
            <TenderMobile
              key={type}
              onChange={onChange}
              isMissingReason={missingReasons.includes(type)}
              name={name}
              type={type}
              expected={expected}
              counted={counted}
              reason={reason}
              isFirst={i === 0}
              billsTotal={billsTotal}
            />
          ))
        )}
      </Box>
    </div>
  );
};

export default Tenders;
